interface GlossaryTerm {
  term: string;
  definition: string;
}

interface GlossaryGroup {
  label: string;
  terms: GlossaryTerm[];
}

// Definitions describe the actual engine behavior (martech-scanner-utils,
// journey-mapper-utils, channel-health-utils, tag-fire-utils, and the
// backend's scanService) — update alongside those modules, not aspirationally.
const TERMINOLOGY: GlossaryGroup[] = [
  {
    label: 'Architecture confidence',
    terms: [
      {
        term: 'High',
        definition:
          'Unambiguous markers: a modern JS framework together with a headless CMS/content API, ' +
          'or Shopify Hydrogen/Oxygen. The verdict can be stated as fact.'
      },
      {
        term: 'Medium',
        definition:
          'Mixed or partial markers — e.g. a modern framework alongside a traditional CMS ' +
          '(could be a headless frontend or embedded components), or a content API without a ' +
          'framework marker. Verify during onboarding.'
      },
      {
        term: 'Low',
        definition:
          'Weak or no markers — often a single generic signal or a scan that saw very little ' +
          'HTML (single-page apps, bot-blocked scans). Treat the verdict as a hypothesis.'
      }
    ]
  },
  {
    label: 'Finding severities',
    terms: [
      {
        term: 'RISK',
        definition:
          'Something broken or exposed today — e.g. no consent management platform on a site ' +
          'buying regulated-market media.'
      },
      {
        term: 'OPPORTUNITY',
        definition:
          'A capability the stack lacks that would measurably improve signal or performance — ' +
          'e.g. no server-side tagging, Meta Pixel without a Conversions API.'
      },
      {
        term: 'NOTE',
        definition:
          'Context worth knowing that needs confirmation rather than action — e.g. multiple GTM ' +
          'containers, platform-specific checkout constraints.'
      },
      {
        term: 'GAP (journey)',
        definition:
          'A journey milestone that needs a detectable capability none of the detected platforms ' +
          'provide. Gaps are ordered revenue-nearest first (Conversion, then Repeat, ' +
          'Consideration, Post-Sale, Awareness).'
      },
      {
        term: 'OVERLAP',
        definition:
          'Two or more detected platforms provide the same capability — a consolidation ' +
          'candidate, not automatically a problem.'
      }
    ]
  },
  {
    label: 'Journey milestone statuses',
    terms: [
      {
        term: 'Covered (✓)',
        definition: 'A detected platform can power or measure this milestone; the tools are listed.'
      },
      {
        term: 'Gap (△)',
        definition:
          'The milestone needs a detectable capability that nothing in the stack provides.'
      },
      {
        term: 'Discovery (○)',
        definition:
          'Not observable in a client-side scan (CRM stages, call tracking, offline imports, ' +
          'in-store, app events) — confirmed in onboarding rather than flagged as a gap.'
      },
      {
        term: 'Coverage %',
        definition:
          'Covered ÷ detectable milestones. Discovery milestones are excluded from the ' +
          'denominator, so the number is defensible in front of a client.'
      }
    ]
  },
  {
    label: 'Channel health scores',
    terms: [
      {
        term: 'Strong / Moderate / Needs attention',
        definition:
          'Each channel check scores healthy = 1, weakness = 0.5, missing = 0 (context-only ' +
          'checks are excluded). Strong is 75%+, Moderate is 40–74%, below that Needs attention.'
      },
      {
        term: 'Limited visibility',
        definition:
          'No scorable checks for this channel in a client-side scan — not a judgment either way.'
      }
    ]
  },
  {
    label: 'Pixel Intel',
    terms: [
      {
        term: 'On the wire (✓)',
        definition:
          'The tracking call was observed as an outbound network request during a runtime ' +
          'capture — the strongest evidence a pixel actually fires.'
      },
      {
        term: 'In code (◦)',
        definition:
          'The call is declared in page/JS source (gtag, fbq, ttq, dataLayer.push, …). It should ' +
          'fire, but consent gates or errors can prevent it.'
      },
      {
        term: 'Configured via tag manager (◇)',
        definition:
          "Found inside a fetched Tag Manager container's own payload (Google's native tag " +
          'types — Google tag config, Ads conversion, GA4 event tags), not a literal call in the ' +
          "page's own HTML/JS. The container is set up to fire this, but nothing observable " +
          'confirms it does — trigger conditions, consent gates, or a paused tag could all still ' +
          'block it. Weaker than "in code"; only covers Google\'s own tag types, not third-party ' +
          'pixels wrapped in custom templates.'
      },
      {
        term: 'Canonical event',
        definition:
          "Each platform's native event vocabulary (purchase / Purchase / CompletePayment / " +
          'checkout) mapped onto one shared funnel vocabulary so platforms can be compared ' +
          'side by side.'
      }
    ]
  },
  {
    label: 'Load speed',
    terms: [
      {
        term: 'Server response',
        definition:
          'Time from request start until response headers arrived (a TTFB equivalent), measured ' +
          'from the scanner. Badged green under 400 ms, amber to 1,000 ms, red above.'
      },
      {
        term: 'HTML download',
        definition: 'Time until the full HTML document finished downloading, same vantage point.'
      },
      {
        term: 'Why not Core Web Vitals',
        definition:
          'LCP, CLS, and INP require rendering the page in a real browser. The static scan ' +
          'measures network fetch only; the runtime-browser engine is what would add CWV.'
      }
    ]
  },
  {
    label: 'Site health & discoverability',
    terms: [
      {
        term: 'Sitemap index',
        definition:
          'A sitemap of sitemaps. Large sites split URLs across child sitemaps so search ' +
          'consoles report indexation rate per child — which is how indexation problems ' +
          'become visible. A single sitemap past ~2,000 URLs is flagged as a splitting ' +
          'opportunity.'
      },
      {
        term: 'Sitemap manager',
        definition:
          'Best-effort guess at what generates the sitemap (Yoast/Rank Math/WordPress core/' +
          'platform-native/manual), read from generator hints in the sitemap file and its URL.'
      },
      {
        term: 'AI/LLM crawler access',
        definition:
          'Whether robots.txt admits the major AI crawlers (GPTBot, ClaudeBot, PerplexityBot, ' +
          'CCBot, Google-Extended, Bytespider, meta-externalagent). Blocking them is a policy ' +
          'choice, not an error — but it should be a deliberate one.'
      },
      {
        term: 'llms.txt',
        definition:
          'An emerging standard file that curates canonical content for AI assistants, the way ' +
          'robots.txt steers crawlers.'
      },
      {
        term: 'JS rendering verdicts',
        definition:
          'Heuristic from each page’s initial HTML (before any JavaScript runs): 2,500+ text ' +
          'characters and 10+ links reads as server-rendered; 800+ characters as partial; below ' +
          'that, primary content likely requires JS — which crawlers and AI agents that don’t ' +
          'execute JS never see. The runtime-browser engine would measure the rendered/raw ' +
          'delta exactly.'
      }
    ]
  },
  {
    label: 'Foundations',
    terms: [
      {
        term: 'Website & infrastructure foundation',
        definition:
          "The site's measurement plumbing: analytics, tag management, session replay/UX, and " +
          'consent management.'
      },
      {
        term: 'Media foundation',
        definition:
          'The advertising side: conversion pixels, consent signals (Consent Mode/TCF/GPP), ' +
          'server-side tagging, CDP, attribution, and first-party identity.'
      }
    ]
  }
];

interface MethodologyItem {
  title: string;
  body: string;
}

const METHODOLOGY: MethodologyItem[] = [
  {
    title: 'Which pages are scanned, and why',
    body:
      'The scan always fetches the URL you enter (the seed), then discovers candidates from the ' +
      "seed page's same-domain links and the site's sitemap.xml. Candidates are prioritized by " +
      'martech signal: checkout and cart pages (fire pixels the homepage never does), product ' +
      'and collection/category pages (dynamic remarketing tags), pricing/plans, ' +
      'contact/demo/lead/signup pages, then about pages — up to 8 pages total including the ' +
      'seed. The whole crawl runs inside a ~20-second budget (kept well under the platform’s ' +
      'request timeout, with margin for a slow-but-real host); a slow site degrades to a partial ' +
      'scan (noted in the report) rather than a hard failure. Every discovered URL passes an SSRF ' +
      'guard, and asset URLs (images, scripts, PDFs) are never crawled as pages.'
  },
  {
    title: 'How platforms are detected',
    body:
      'Each fetched page contributes evidence: its HTML, inline scripts, script sources, and a ' +
      'capped, de-duplicated sample of first-party JS bundles. A signature database of ' +
      'per-platform patterns is matched against that evidence, so every detection is backed by ' +
      'concrete matches. Presence findings are verified; absence findings are client-side ' +
      'observations only — server-to-server integrations (e.g. a Conversions API) can exist ' +
      'with no browser footprint.'
  },
  {
    title: 'How the journey is aligned and scored',
    body:
      "The client's vertical selects a journey template (with the vertical's typical client " +
      'type as default), and each detected platform is classified into a capability (paid ' +
      'media, lifecycle, commerce, analytics, …). Each milestone in the template is then marked ' +
      'covered, gap, or discovery by matching its required capabilities against the detected ' +
      'ones. Stage and overall percentages count detectable milestones only. Milestones are ' +
      'editable; exports reflect edits.'
  },
  {
    title: 'How discoverability is checked',
    body:
      'Alongside the page crawl, the scan fetches robots.txt (declared sitemaps, whether ' +
      'Googlebot or major AI crawlers are blocked site-wide), the sitemap itself (index vs. ' +
      'single file, child-sitemap count, a sampled URL count, and a manager guess from ' +
      'generator hints), and llms.txt. JavaScript-rendering dependency is estimated from each ' +
      'crawled page’s initial HTML — the content that exists before any script runs, which is ' +
      'what non-rendering crawlers actually see.'
  },
  {
    title: 'Bot-blocked and rate-limited scans',
    body:
      'Some sites answer automated fetches with a challenge page (HTTP 403/429). The scan keeps ' +
      'the block page as evidence but flags the report with a warning, because findings from a ' +
      'challenge page are near-meaningless: thin HTML, no links to crawl, and an ' +
      '"Undetermined" architecture read. Blocked secondary pages are skipped entirely so their ' +
      'challenge bodies never pollute the evidence. A runtime-browser scan is the reliable ' +
      'path past such walls.'
  },
  {
    title: 'What a static scan cannot see',
    body:
      'Tags injected at runtime by tag managers, events that fire only on interaction or deeper ' +
      'funnel steps (checkout, lead forms), consent-gated tags, and anything server-side. Those ' +
      'appear once the runtime-browser engine captures live network traffic — which also ' +
      'upgrades Pixel Intel from "declared in code" / "configured via tag manager" to "observed ' +
      'on the wire" and adds Core Web Vitals.'
  },
  {
    title: 'Tags routed through Tag Manager',
    body:
      'Most sites don’t call gtag()/fbq() directly — Google Tag Manager’s own runtime ' +
      'dispatches them from its container configuration, which never appears as a literal call ' +
      'in the page’s HTML/JS. To see these, the scan fetches each distinct GTM container’s ' +
      'own public payload (the same file every visitor’s browser downloads) and decodes ' +
      'Google’s own native tag types: the Google tag config (which sends a page_view ' +
      'automatically), Ads conversion tags, and GA4 event tags. Third-party pixels wrapped in ' +
      'custom GTM templates aren’t decoded this way and stay invisible until a runtime scan.'
  }
];

export const GlossaryView = () => (
  <div className='digital-audit-page'>
    <div className='digital-audit-section'>
      <div className='digital-audit-section-head'>
        <div className='digital-audit-section-title'>Terminology</div>
        <div className='digital-audit-section-count'>what the labels in this report mean</div>
      </div>
      <div className='digital-audit-glossary-grid'>
        {TERMINOLOGY.map(group => (
          <div key={group.label} className='digital-audit-glossary-group'>
            <span className='digital-audit-obs-group-label'>{group.label}</span>
            {group.terms.map(t => (
              <div key={t.term} className='digital-audit-glossary-term'>
                <div className='digital-audit-glossary-word'>{t.term}</div>
                <div className='digital-audit-glossary-def'>{t.definition}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>

    <div className='digital-audit-section'>
      <div className='digital-audit-section-head'>
        <div className='digital-audit-section-title'>Methodology</div>
        <div className='digital-audit-section-count'>how the scan works, in plain terms</div>
      </div>
      {METHODOLOGY.map(item => (
        <div key={item.title} className='digital-audit-glossary-method'>
          <div className='digital-audit-glossary-word'>{item.title}</div>
          <div className='digital-audit-glossary-def'>{item.body}</div>
        </div>
      ))}
    </div>
  </div>
);
